import { useWorkspaceContext } from "../../../shared/hooks/useWorkspaceContext";
import { useState } from "react";
import { Link } from "react-router";
import { FileWarning, ShieldCheck } from "lucide-react";
import { Badge, Button, Card } from "@insights/ui";
import { provenanceSignature, type ProvenanceResponse, type ProvenanceTransformation } from "@insights/contracts";
import { useProvenance } from "../hooks/useProvenance";

function effectLabel(effect: ProvenanceTransformation["preservation"]): string {
  switch (effect) {
    case "preserved":
      return "Provenance preserved";
    case "broken":
      return "Provenance broken";
    case "unaffected":
      return "Provenance unaffected";
  }
}

export function ProvenanceReportView({ report, workspaceId }: { report: ProvenanceResponse; workspaceId: string | undefined }) {
  const percentage = report.total > 0 ? Math.round(report.verified / report.total * 100) : 0;
  return (
    <div className="provenance-report">
      <Card className="provenance-summary">
        <div className="provenance-summary-heading">
          <ShieldCheck aria-hidden="true" />
          <div>
            <h3>Artifact provenance</h3>
            <p>{report.verified} of {report.total} artifacts verified{report.total > 0 ? ` (${percentage}%)` : ""}.</p>
          </div>
          {report.total > report.verified && (
            <Badge tone="warning">{report.total - report.verified} need attention</Badge>
          )}
        </div>
        <div className="provenance-coverage" aria-hidden="true">
          <span style={{ width: `${percentage}%` }} />
        </div>
        <p className="provenance-summary-note">Verification requires complete source, transformation and dependency evidence. Missing reports count as non-verified.</p>
      </Card>
      {report.total === 0 && <p>No successful artifact productions or reuse associations are available.</p>}
      {report.artifacts.map((artifact) => (
        <Card className="provenance-artifact" key={`${artifact.projectId}:${artifact.target}`}>
          <div className="provenance-artifact-heading">
            <div className="provenance-artifact-identity">
              <h3>{artifact.project}</h3>
              <Badge>{artifact.target}</Badge>
            </div>
            <div className="provenance-artifact-status">
              <Badge tone={artifact.status === "VERIFIED" ? "success" : artifact.status === "INCOMPLETE" ? "danger" : "warning"}>{artifact.status}</Badge>
              {artifact.status !== "VERIFIED" && <span className="provenance-attention">Needs attention</span>}
            </div>
          </div>
          <div className="provenance-artifact-meta">
            <span>{artifact.association === "produced" ? "Produced in this build" : artifact.association === "reused" ? "Reused from cache" : "Carried forward"}</span>
            <Link to={`/w/${workspaceId}/builds/${artifact.buildId}`}>View build →</Link>
          </div>
          {!artifact.report && (
            <div className="provenance-missing">
              <FileWarning aria-hidden="true" />
              <div>
                <strong>Missing provenance report</strong>
                <p>Produce this artifact with a provenance-enabled Terrabuild client.</p>
              </div>
            </div>
          )}
          {artifact.report && (
            <>
              <dl className="provenance-production">
                <div><dt>Production</dt><dd><code>{artifact.report.productionId}</code></dd></div>
                <div><dt>Produced</dt><dd>{new Date(artifact.report.producedAt).toLocaleString()}</dd></div>
                <div><dt>Producer</dt><dd>Terrabuild {artifact.report.producerVersion}</dd></div>
              </dl>
              {artifact.producerBuildId && <Link to={`/w/${workspaceId}/builds/${artifact.producerBuildId}`}>View original production build</Link>}
              <p>Source revision: <code>{artifact.report.revision}</code></p>
              <h4>Source evidence</h4>
              <ul>{artifact.report.evidence.map((evidence, index) => (
                <li key={index}>{evidence.kind}: {evidence.status} · {evidence.reference}<br />{evidence.reason}{evidence.hash && <><br /><code>{evidence.hash}</code></>}</li>
              ))}</ul>
              <h4>Transformations</h4>
              <ul>{artifact.report.transformations.map((transformation) => (
                <li key={`${transformation.nodeId}:${transformation.step}`}>
                  {transformation.step} · {transformation.entrypoint} · {effectLabel(transformation.preservation)} · {transformation.origin === "builtin" ? "Built-in extension declaration" : "Custom self-declaration; untrusted"}
                  <br /><code>{transformation.contractFingerprint}</code>
                </li>
              ))}</ul>
              <h4>Dependency productions</h4>
              <ul>{artifact.report.dependencies.map((dependency) => (
                <li key={dependency.nodeId}>{dependency.nodeId}: {dependency.status} · {dependency.productionId ?? "Missing production report"}</li>
              ))}</ul>
              {artifact.report.causes.length > 0 && <>
                <h4>Evidence gaps</h4>
                <ul>{artifact.report.causes.map((cause, index) => (
                  <li key={index}><strong>{cause.nodeId}{cause.step ? ` · ${cause.step}` : ""}</strong>: {cause.reason}<br />{cause.recommendation}</li>
                ))}</ul>
              </>}
            </>
          )}
        </Card>
      ))}
      {(report.relatedProductions ?? []).map((production) => (
        <Card className="provenance-artifact" key={production.report.productionId}>
          <h3>Upstream: {production.report.nodeId}</h3>
          <Badge tone={production.report.status === "VERIFIED" ? "success" : "danger"}>{production.report.status}</Badge>
          <p><Link to={`/w/${workspaceId}/builds/${production.buildId}`}>View original production build</Link> · {production.report.productionId}</p>
          <p>Source revision: <code>{production.report.revision}</code></p>
          <ul>{production.report.evidence.map((evidence, index) => <li key={index}>{evidence.reference}: {evidence.status} · {evidence.reason}<br /><code>{evidence.hash}</code></li>)}</ul>
          <ul>{production.report.transformations.map((transformation) => <li key={transformation.step}>{transformation.step} · {effectLabel(transformation.preservation)} · {transformation.origin === "builtin" ? "Built-in extension declaration" : "Custom self-declaration"}<br /><code>{transformation.contractFingerprint}</code></li>)}</ul>
        </Card>
      ))}
    </div>
  );
}

export function ProvenancePanel({ workspaceId, buildId, state = false, initiallyOpen = false, collapsible = true }: {
  workspaceId: string | undefined;
  buildId: string;
  state?: boolean;
  initiallyOpen?: boolean;
  collapsible?: boolean;
}) {
  const workspace = useWorkspaceContext(workspaceId);
  const enabled = workspace.data?.features.provenance === true;
  const [open, setOpen] = useState(initiallyOpen);
  const visible = !collapsible || open;
  const report = useProvenance(workspaceId, buildId, state, enabled && visible);
  if (!enabled) {
    return null;
  }
  return (
    <section aria-label={state ? "Environment provenance" : "Build provenance"}>
      {collapsible && <Button color="neutral" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? "Hide provenance" : "View provenance"}</Button>}
      {visible && report.isLoading && <p role="status">Loading provenance…</p>}
      {visible && report.isError && <p role="alert">Provenance could not be loaded. <Button onClick={() => report.refetch()}>Retry</Button></p>}
      {visible && report.data && <ProvenanceReportView report={report.data} workspaceId={workspaceId} />}
    </section>
  );
}

export function ProvenanceComparison({ workspaceId, fromBuildId, toBuildId }: {
  workspaceId: string | undefined;
  fromBuildId: string;
  toBuildId: string;
}) {
  const workspace = useWorkspaceContext(workspaceId);
  const enabled = workspace.data?.features.provenance === true;
  const [open, setOpen] = useState(false);
  const previous = useProvenance(workspaceId, fromBuildId, true, enabled && open);
  const current = useProvenance(workspaceId, toBuildId, true, enabled && open);
  const keys = new Set([
    ...(previous.data?.artifacts ?? []).map((artifact) => `${artifact.projectId}:${artifact.target}`),
    ...(current.data?.artifacts ?? []).map((artifact) => `${artifact.projectId}:${artifact.target}`),
  ]);
  if (!enabled) {
    return null;
  }
  return (
    <section aria-label="Provenance comparison">
      <Button color="neutral" aria-expanded={open} onClick={() => setOpen(!open)}>Compare provenance</Button>
      {open && (previous.isLoading || current.isLoading) && <p role="status">Loading both provenance states…</p>}
      {open && (previous.isError || current.isError) && <p role="alert">Provenance comparison could not be loaded. <Button onClick={() => { void previous.refetch(); void current.refetch(); }}>Retry</Button></p>}
      {open && previous.data && current.data && <>
        <h3>Provenance changes</h3>
        <ul>{[...keys].map((key) => {
          const before = previous.data.artifacts.find((artifact) => `${artifact.projectId}:${artifact.target}` === key);
          const after = current.data.artifacts.find((artifact) => `${artifact.projectId}:${artifact.target}` === key);
          const changed = !before || !after || provenanceSignature(before) !== provenanceSignature(after);
          if (!changed) {
            return null;
          }
          return <li key={key}>{key}: {before?.status ?? "Absent"} → {after?.status ?? "Removed"}{before?.status === "VERIFIED" && after && after.status !== "VERIFIED" ? " · Provenance degraded" : " · Evidence changed"}</li>;
        })}</ul>
        <h3>From: complete state</h3>
        <ProvenanceReportView report={previous.data} workspaceId={workspaceId} />
        <h3>To: complete state</h3>
        <ProvenanceReportView report={current.data} workspaceId={workspaceId} />
      </>}
    </section>
  );
}
